import { availabilitiesAtom, categoriesAtom, cartAtom } from "@/atoms"
import { Callout, SelectableCardGroup } from "@/components/ui"
import { formatSlotChoiceParts, formatWindowOpening } from "@/lib/scheduling/format"
import { filterSlotsForCart, formatSlotRestriction } from "@/lib/scheduling/filter-slots"
import type { SchedulingChannel, SchedulingSlot } from "@/lib/scheduling/types"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { useAtomValue } from "jotai"
import { useMemo } from "react"
import { SlotOption, SlotPickerSection } from "./SlotPickerSection"

type ScheduledSlotPickerProps = {
  channel: SchedulingChannel
  selected: SchedulingSlot | null
  onSelect: (slot: SchedulingSlot) => void
  /** Delivery ETA of the chosen address, used only for the arrival estimate. */
  deliveryEtaMinutes?: number
}

/**
 * The offered grid of the target window, minus the slots some bag line cannot
 * honour (UC5). The hidden slots are explained instead of vanishing silently,
 * and the checkout remains the authority over what is accepted.
 */
export function ScheduledSlotPicker({
  channel,
  selected,
  onSelect,
  deliveryEtaMinutes,
}: ScheduledSlotPickerProps) {
  const cart = useAtomValue(cartAtom)
  const availabilities = useAtomValue(availabilitiesAtom)
  const categories = useAtomValue(categoriesAtom)
  const { timezone } = useStorefrontScheduling()

  const { slots, restrictions } = useMemo(
    () => filterSlotsForCart(channel.slots, cart, availabilities, categories),
    [channel.slots, cart, availabilities, categories],
  )

  const windowLabel = channel.targetWindow
    ? `Janela que abre ${formatWindowOpening(channel.targetWindow.startsAt, new Date(), timezone ?? undefined)}`
    : null

  return (
    <SlotPickerSection subtitle={windowLabel}>
      {restrictions.length > 0 ? (
        <Callout tone="warning" title="Alguns horários não servem para a sua sacola" className="mb-3">
          <ul className="space-y-1">
            {restrictions.map((restriction) => (
              <li key={restriction.name}>{formatSlotRestriction(restriction)}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {slots.length === 0 ? (
        <Callout tone="error" title="Nenhum horário disponível">
          Nenhum horário desta janela atende a todos os itens da sacola. Remova um item ou volte
          mais tarde.
        </Callout>
      ) : (
        <SelectableCardGroup
          label="Horários disponíveis"
          value={selected?.readyAt ?? null}
          onValueChange={(readyAt) => {
            const slot = slots.find((candidate) => candidate.readyAt === readyAt)
            if (slot) onSelect(slot)
          }}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {slots.map((slot) => (
            <ScheduledSlot
              key={slot.readyAt}
              {...formatSlotChoiceParts(slot, channel.serviceType, deliveryEtaMinutes, timezone ?? undefined)}
              value={slot.readyAt}
            />
          ))}
        </SelectableCardGroup>
      )}
    </SlotPickerSection>
  )
}

function ScheduledSlot({ value, title, subtitle }: { value: string; title: string; subtitle?: string }) {
  return (
    <SlotOption value={value} subtitle={subtitle}>
      {title}
    </SlotOption>
  )
}
